/**
 * Which rejections came out of the session guard's own mint (auth-11 P2-2).
 *
 * Since PR-2 the Axios interceptor propagates `recover()`'s OWN failure rather than the
 * refusal that triggered it — the honest answer, and the only one that does not tell a
 * cache subscriber a session is over when the mint was merely unreachable. The cost is
 * that the failure is usually uncoded (a `NetworkError` or a 500), so the toast predicate
 * has nothing to recognise it by, and ONE outage becomes one red box per background query:
 * the guard shares a single mint between every caller that joined it, rethrows that one
 * error to all of them, and rethrows it again for every refusal inside the 30s cooldown.
 * The guard already reports the outage once, or deliberately stays silent about a pure
 * connectivity fault — this is the flag that lets per-request consumers respect that
 * decision instead of overriding it. Sentry 6Q/6R is the shape: an iPhone tab backgrounded
 * mid-renewal, and the wake-up refetch meeting refused cookies.
 *
 * A `WeakSet` rather than a property on the error: the class shape and every `instanceof`
 * stay exactly as they were — `isConnectivityError` and `isSessionOverError` must keep
 * seeing the same typed error — and the shared object the guard hands to many callers is
 * marked once, for all of them, with no entry outliving the error itself.
 */
const renewalFailures = new WeakSet<object>();

/** Records that this rejection is the mint's own failure. Non-objects are not tracked. */
export function markRenewalFailure(error: unknown) {
  if (typeof error === 'object' && error !== null) renewalFailures.add(error);
}

export function isRenewalFailure(error: unknown) {
  return (
    typeof error === 'object' && error !== null && renewalFailures.has(error)
  );
}
